import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Chip,
  Box,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Switch,
  CircularProgress,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { AddCircleOutline, Delete } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { createFlowCampaign, updateFlowCampaign } from "../../services/flowCampaigns.api";
import { COMPACT_MENU } from "../BaileysConnectionModal/compactMenu";
import { FLOW_CHANNELS, FLOW_CHANNEL_META, channelOfFlow, flowFitsConnection } from "../../constants/flowChannels";

/**
 * Crear / editar una campaña de flujo (doc 0438 Parte VII), con la maqueta compacta por secciones del
 * modal de Baileys (0439). Antes: un selector con TODOS los flujos de todos los canales en una lista sin
 * fin y las conexiones en un desplegable múltiple.
 *
 * Orden: canal → flujo (con buscador, solo los de ese canal) → frases → conexiones del canal → activa.
 * Cambiar de canal vacía el flujo y las conexiones: una campaña no mezcla canales (lo exige el backend).
 */
const useStyles = makeStyles((theme) => ({
  paper: { maxWidth: 640 },
  title: { padding: theme.spacing(1.5, 2.5), "& h2": { fontSize: "1.05rem", fontWeight: 600, display: "flex", alignItems: "center", gap: 10 } },
  content: {
    padding: theme.spacing(1, 2.5, 2),
    "& .MuiOutlinedInput-root": { fontSize: "0.85rem" },
    "& .MuiInputLabel-outlined": { fontSize: "0.85rem" },
  },
  section: {
    margin: theme.spacing(2, 0, 0.75),
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
  },
  hint: { display: "block", marginTop: 4, lineHeight: 1.35, color: theme.palette.text.secondary, fontSize: 12 },
  frase: { display: "flex", gap: theme.spacing(1), marginBottom: theme.spacing(1), alignItems: "center" },
  lista: { maxHeight: 180, overflow: "auto", border: `1px solid ${theme.palette.divider}`, borderRadius: 8, padding: "2px 8px" },
  actions: { padding: theme.spacing(1.25, 2.5) },
}));

const VACIO = { name: "", flowId: null, phrase: [{ text: "", type: "exact" }], whatsappIds: [], status: true };

const TIPOS = [
  ["exact", "flowcampaigns.form.phraseTypes.exact", "Exacta"],
  ["contains", "flowcampaigns.form.phraseTypes.contains", "Contiene"],
  ["startsWith", "flowcampaigns.form.phraseTypes.startsWith", "Comienza con"],
  ["endsWith", "flowcampaigns.form.phraseTypes.endsWith", "Termina con"],
];

const t = (clave, defecto) => i18n.t(clave) || defecto;

const FlowCampaignModal = ({ open, campaign, flows, whatsApps, onClose, onSaved }) => {
  const classes = useStyles();
  const [form, setForm] = useState(VACIO);
  const [canal, setCanal] = useState("baileys");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (campaign) {
      const flujo = flows.find((f) => Number(f.id) === Number(campaign.flowId));
      setCanal(channelOfFlow(flujo?.provider));
      setForm({
        name: campaign.name || "",
        flowId: campaign.flowId || null,
        phrase: campaign.phrase?.length ? campaign.phrase : VACIO.phrase,
        whatsappIds: campaign.whatsappIds || [],
        status: campaign.status !== undefined ? Boolean(campaign.status) : true,
      });
    } else {
      setCanal("baileys");
      setForm(VACIO);
    }
  }, [open, campaign, flows]);

  const meta = FLOW_CHANNEL_META[canal];
  const flujosDelCanal = useMemo(() => flows.filter((f) => channelOfFlow(f.provider) === canal), [flows, canal]);
  const flujoElegido = flujosDelCanal.find((f) => Number(f.id) === Number(form.flowId)) || null;
  const conexionesDelCanal = useMemo(() => (whatsApps || []).filter((w) => flowFitsConnection(canal, w.provider)), [whatsApps, canal]);

  const cambiarCanal = (c) => {
    if (c === canal) return;
    setCanal(c);
    setForm((f) => ({ ...f, flowId: null, whatsappIds: [] }));
  };

  const cambiarFrase = (i, campo, valor) =>
    setForm((f) => ({ ...f, phrase: f.phrase.map((p, j) => (j === i ? { ...p, [campo]: valor } : p)) }));

  const guardar = async () => {
    const frases = form.phrase.filter((p) => p.text.trim());
    if (!form.name.trim()) return toast.error(t("flowcampaigns.validation.nameRequired", "El nombre de la campaña es requerido"));
    if (!form.flowId) return toast.error(t("flowcampaigns.validation.flowRequired", "Debe seleccionar un flujo"));
    if (!frases.length) return toast.error(t("flowcampaigns.validation.phraseRequired", "Debe proporcionar al menos una frase de activación"));
    if (!form.whatsappIds.length) return toast.error("Elige al menos una conexión");

    const datos = {
      name: form.name.trim(),
      flowId: Number(form.flowId),
      phrase: frases.map((p) => ({ text: p.text.trim(), type: p.type })),
      whatsappIds: form.whatsappIds,
      status: form.status,
    };
    try {
      setSaving(true);
      if (campaign) {
        await updateFlowCampaign(campaign.id, datos);
        toast.success(t("flowcampaigns.toasts.updated", "Campaña actualizada exitosamente"));
      } else {
        await createFlowCampaign(datos);
        toast.success(t("flowcampaigns.toasts.created", "Campaña creada exitosamente"));
      }
      onSaved();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth scroll="paper" classes={{ paper: classes.paper }}>
      <DialogTitle className={classes.title}>
        <img src={meta.icon} alt="" style={{ width: 26, height: 26 }} />
        {campaign ? t("flowcampaigns.form.editTitle", "Editar campaña") : t("flowcampaigns.form.title", "Nueva campaña")}
      </DialogTitle>
      <DialogContent dividers className={classes.content}>
        <TextField
          label={t("flowcampaigns.form.name", "Nombre de la campaña")}
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          variant="outlined"
          size="small"
          fullWidth
          required
          autoFocus
        />

        <Typography className={classes.section}>Canal</Typography>
        <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
          {FLOW_CHANNELS.map((c) => (
            <Chip
              key={c}
              clickable
              onClick={() => cambiarCanal(c)}
              avatar={<img src={FLOW_CHANNEL_META[c].icon} alt="" style={{ width: 18, height: 18 }} />}
              label={FLOW_CHANNEL_META[c].label}
              variant={canal === c ? "default" : "outlined"}
              style={canal === c ? { backgroundColor: FLOW_CHANNEL_META[c].color, color: "#fff" } : undefined}
            />
          ))}
        </Box>
        <span className={classes.hint}>Una campaña es de un solo canal: su flujo y sus conexiones son de ese canal.</span>

        <Typography className={classes.section}>Flujo</Typography>
        <Autocomplete
          options={flujosDelCanal}
          value={flujoElegido}
          onChange={(_, f) => setForm({ ...form, flowId: f ? f.id : null })}
          getOptionLabel={(f) => f?.name || ""}
          getOptionSelected={(a, b) => Number(a.id) === Number(b.id)}
          noOptionsText={`No hay flujos de ${meta.label}. Créalo en «Flujos Automatizados».`}
          ListboxProps={{ style: { maxHeight: 260 } }}
          size="small"
          renderInput={(params) => (
            <TextField {...params} variant="outlined" placeholder={`Busca un flujo de ${meta.label}…`} />
          )}
        />

        <Typography className={classes.section}>{t("flowcampaigns.form.phrases", "Frases de activación")}</Typography>
        {form.phrase.map((p, i) => (
          <Box key={i} className={classes.frase}>
            <TextField
              placeholder="Texto que escribe el cliente"
              value={p.text}
              onChange={(e) => cambiarFrase(i, "text", e.target.value)}
              variant="outlined"
              size="small"
              style={{ flex: 1 }}
            />
            <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
              <Select value={p.type} onChange={(e) => cambiarFrase(i, "type", e.target.value)} MenuProps={COMPACT_MENU}>
                {TIPOS.map(([v, clave, defecto]) => (
                  <MenuItem key={v} value={v}>{t(clave, defecto)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton
              size="small"
              disabled={form.phrase.length === 1}
              onClick={() => setForm((f) => ({ ...f, phrase: f.phrase.filter((_, j) => j !== i) }))}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Button size="small" startIcon={<AddCircleOutline />} onClick={() => setForm((f) => ({ ...f, phrase: [...f.phrase, { text: "", type: "exact" }] }))}>
          {t("flowcampaigns.form.addPhrase", "Agregar frase")}
        </Button>

        <Typography className={classes.section}>Conexiones de {meta.label}</Typography>
        {conexionesDelCanal.length === 0 ? (
          <Typography variant="caption" color="error">
            No hay conexiones de {meta.long}. Crea una en «Conexiones» para poder usar esta campaña.
          </Typography>
        ) : (
          <div className={classes.lista}>
            {conexionesDelCanal.map((w) => (
              <FormControlLabel
                key={w.id}
                style={{ display: "flex", margin: 0 }}
                control={
                  <Checkbox
                    size="small"
                    color="primary"
                    checked={form.whatsappIds.includes(w.id)}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        whatsappIds: e.target.checked ? [...f.whatsappIds, w.id] : f.whatsappIds.filter((x) => x !== w.id),
                      }))
                    }
                  />
                }
                label={
                  <Typography variant="body2">
                    {w.name}
                    {w.status !== "CONNECTED" && <span style={{ opacity: 0.6 }}> · desconectada</span>}
                  </Typography>
                }
              />
            ))}
          </div>
        )}

        <Box mt={1.5}>
          <FormControlLabel
            control={<Switch size="small" color="primary" checked={form.status} onChange={(e) => setForm({ ...form, status: e.target.checked })} />}
            label={<Typography variant="body2">{t("flowcampaigns.form.status", "Campaña activa")}</Typography>}
          />
        </Box>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button size="small" onClick={onClose} disabled={saving}>{t("flowcampaigns.form.cancel", "Cancelar")}</Button>
        <Button size="small" color="primary" variant="contained" onClick={guardar} disabled={saving}>
          {campaign ? t("flowcampaigns.form.update", "Actualizar") : t("flowcampaigns.form.save", "Crear")}
          {saving && <CircularProgress size={16} style={{ marginLeft: 8 }} />}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowCampaignModal;
